import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import AccountLayout from '@/components/feature/AccountLayout';
import { useAuth } from '@/hooks/useAuth';
import { fetchMyDemands, closeDemand, submitDemandAudit } from '@/service/api/demand';

function formatDate(dateStr?: string) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function getStatusInfo(status?: string) {
  const s = (status || '').toUpperCase();
  if (s === 'PENDING_REVIEW' || s === 'PENDING' || s === 'IN_AUDIT') {
    return { label: '审核中', color: 'bg-accent-100 text-accent-700', type: 'in_audit' };
  }
  if (s === 'REJECTED' || s === 'AUDIT_REJECTED') {
    return { label: '已驳回', color: 'bg-red-100 text-red-600', type: 'rejected' };
  }
  if (s === 'RECRUITING' || s === 'PUBLISHED' || s === 'APPROVED') {
    return { label: '已发布', color: 'bg-green-100 text-green-700', type: 'published' };
  }
  if (s === 'DRAFT') {
    return { label: '草稿', color: 'bg-foreground-100 text-foreground-600', type: 'draft' };
  }
  if (s === 'CLOSED') {
    return { label: '已关闭', color: 'bg-red-50 text-red-600', type: 'closed' };
  }
  if (s === 'EXPIRED') {
    return { label: '已过期', color: 'bg-foreground-100 text-foreground-500', type: 'expired' };
  }
  return { label: status || '未知', color: 'bg-foreground-100 text-foreground-500', type: 'other' };
}

export default function MyPostingsPage() {
  const [activeStatus, setActiveStatus] = useState<string>('all');
  const [postings, setPostings] = useState<Api.Demand.DemandResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const { user, rawUser, isEnterprise } = useAuth();
  const isEnterpriseVerified = isEnterprise && user?.verified;
  const isNotSubmitted = !isEnterpriseVerified || rawUser?.market_residency_status === 'NOT_SUBMITTED';

  const loadDemands = useCallback(async () => {
    if (isNotSubmitted) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setLoadError('');
    try {
      const res = await fetchMyDemands();
      setPostings(res?.items || []);
    } catch (err) {
      setLoadError('需求发布记录加载失败，请稍后重试');
    } finally {
      setIsLoading(false);
    }
  }, [isNotSubmitted]);

  useEffect(() => {
    loadDemands();
  }, [loadDemands]);

  const handleCloseDemand = async (id?: string) => {
    if (!id) return;
    try {
      await closeDemand(id);
      loadDemands();
    } catch (error) {
      console.error('关闭需求失败:', error);
    }
  };

  const handleSubmitAudit = async (id?: string) => {
    if (!id) return;
    try {
      await submitDemandAudit(id);
      loadDemands();
    } catch (error) {
      console.error('提交审核失败:', error);
    }
  };

  const filteredPostings = postings.filter((post) => {
    const info = getStatusInfo(post.status);
    if (activeStatus === 'all') return true;
    if (activeStatus === 'in_audit') return info.type === 'in_audit';
    if (activeStatus === 'audit_rejected') return info.type === 'rejected';
    if (activeStatus === 'published') return info.type === 'published';
    if (activeStatus === 'draft') return info.type === 'draft';
    if (activeStatus === 'closed') return info.type === 'closed';
    return true;
  });

  return (
    <AccountLayout
      activePath="/my-postings"
      breadcrumbs={[{ label: '账号中心', href: '/profile' }, { label: '需求发布' }]}
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading font-bold text-xl text-foreground-950">需求发布记录</h1>
          <p className="text-xs text-foreground-500 mt-1">管理您发布的需求，发布后需经平台审核</p>
        </div>
        {!isNotSubmitted && (
          <Link
            to="/post-demand"
            className="px-4 py-2 rounded-lg bg-primary-500 text-foreground-950 text-sm font-medium hover:bg-primary-400 transition-all cursor-pointer whitespace-nowrap"
          >
            发布需求
          </Link>
        )}
      </div>

      {isNotSubmitted ? (
        /* 未提交市场入驻/未认证企业提示 */
        <div className="glass-card rounded-2xl p-10 text-center border border-background-200">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-50 flex items-center justify-center">
            <i className="ri-shield-user-line text-3xl text-amber-500"></i>
          </div>
          <h3 className="text-base font-semibold text-foreground-950 mb-2">需要认证企业</h3>
          <p className="text-sm text-foreground-500 max-w-md mx-auto mb-6">
            发布与管理需求需先完成企业认证并入驻市场。请先前往认证页面完成企业认证。
          </p>
          <Link
            to="/certification"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary-500 text-foreground-950 font-medium text-sm hover:bg-primary-400 transition-all shadow-sm"
          >
            前往企业认证
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      ) : (
        <>
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-2 mb-6">
            {[
              { key: 'all', label: '全部' },
              { key: 'in_audit', label: '审核中' },
              { key: 'audit_rejected', label: '已驳回' },
              { key: 'published', label: '已发布' },
              { key: 'draft', label: '草稿' },
              { key: 'closed', label: '已关闭' },
            ].map((s) => (
              <button
                key={s.key}
                onClick={() => setActiveStatus(s.key)}
                className={`text-xs px-3 py-1.5 rounded-full transition-all cursor-pointer whitespace-nowrap ${activeStatus === s.key
                  ? 'bg-foreground-950 text-background-50'
                  : 'border border-background-300/60 text-foreground-500 hover:bg-gray-50'
                  }`}
              >
                {s.label}
              </button>
            ))}
          </div>

          {/* List */}
          <div className="space-y-3">
            {isLoading ? (
              <div className="text-center py-20">
                <div className="w-10 h-10 mx-auto mb-4 rounded-full border-2 border-primary-200 border-t-primary-500 animate-spin"></div>
                <p className="text-sm text-foreground-500">正在加载需求发布记录...</p>
              </div>
            ) : loadError ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
                  <i className="ri-error-warning-line text-2xl text-red-400"></i>
                </div>
                <p className="text-sm text-red-500">{loadError}</p>
              </div>
            ) : filteredPostings.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-sm text-foreground-500">暂无匹配的需求发布记录</p>
              </div>
            ) : (
              filteredPostings.map((post) => {
                const statusInfo = getStatusInfo(post.status);
                return (
                  <div
                    key={post.id}
                    className="glass-card rounded-xl p-5 hover:border-primary-200/50 transition-all"
                  >
                    {/* Top row: type badge + status */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-xs px-2.5 py-1 rounded-full font-medium bg-primary-100 text-primary-700">
                          需求
                        </span>
                        {post.id && <span className="text-xs text-foreground-400">{post.id}</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${statusInfo.color}`}>
                          {statusInfo.label}
                        </span>
                      </div>
                    </div>

                    {/* Title */}
                    <Link
                      to={`/my-demands/${post.id}`}
                      className="text-sm font-semibold text-foreground-950 hover:text-primary-700 transition-colors cursor-pointer line-clamp-1 block mb-2"
                    >
                      {post.title}
                    </Link>

                    {/* Meta info */}
                    <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-400">
                      {post.budget_display && <span>预算: {post.budget_display}</span>}
                      {post.published_at && (
                        <span className="flex items-center gap-1">
                          <i className="ri-calendar-line"></i>发布于 {formatDate(post.published_at)}
                        </span>
                      )}
                      {post.bid_deadline_at && (
                        <span className="flex items-center gap-1">
                          <i className="ri-timer-line"></i>截止 {formatDate(post.bid_deadline_at)}
                        </span>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-background-200/70">
                      <Link
                        to={`/my-demands/${post.id}`}
                        className="px-3 py-1.5 rounded-lg bg-background-100 text-foreground-700 text-xs font-medium hover:bg-primary-50 hover:text-primary-700 transition-all cursor-pointer whitespace-nowrap"
                      >
                        查看详情
                      </Link>
                      {statusInfo.type === 'rejected' && (
                        <Link
                          to={`/post-demand?id=${post.id}`}
                          className="px-3 py-1.5 rounded-lg bg-primary-500 text-foreground-950 text-xs font-medium hover:bg-primary-400 transition-all cursor-pointer whitespace-nowrap"
                        >
                          修改并重新提交
                        </Link>
                      )}
                      {statusInfo.type === 'draft' && (
                        <>
                          <Link
                            to={`/post-demand?id=${post.id}`}
                            className="px-3 py-1.5 rounded-lg bg-primary-500 text-foreground-950 text-xs font-medium hover:bg-primary-400 transition-all cursor-pointer whitespace-nowrap"
                          >
                            继续编辑
                          </Link>
                          <button
                            onClick={() => handleSubmitAudit(post.id)}
                            className="px-3 py-1.5 rounded-lg border border-primary-300/60 text-xs text-primary-700 hover:bg-primary-50 transition-all cursor-pointer whitespace-nowrap"
                          >
                            提交审核
                          </button>
                        </>
                      )}
                      {statusInfo.type === 'published' && (
                        <button
                          onClick={() => handleCloseDemand(post.id)}
                          className="px-3 py-1.5 rounded-lg border border-background-300/60 text-xs text-foreground-500 hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap"
                        >
                          关闭需求
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </>
      )}
    </AccountLayout>
  );
}
